'use client';

/**
 * Tabs — in-page tabs held in state (not the URL). For tabs that should
 * deep-link (`?view=…`), use PageTabs; both follow the same keyboard model.
 *
 *     <Tabs
 *       label="Matter sections"
 *       tabs={[
 *         { key: 'overview', label: 'Overview', render: () => <Overview /> },
 *         { key: 'docs', label: 'Documents', badge: 12, render: () => <Docs /> },
 *       ]}
 *     />
 *
 * Controlled with `value` + `onChange`, or uncontrolled with `defaultValue`.
 * WAI-ARIA tabs: role tablist/tab/tabpanel, roving tabindex, ArrowLeft/Right
 * (wrapping), Home, End; disabled tabs are skipped. Selection follows focus.
 */
import { type KeyboardEvent, type ReactNode, useId, useRef, useState } from 'react';

import { cn } from './cn';
import { nextTabIndex } from './tabsKeyboard';

export interface TabItem<K extends string = string> {
  key: K;
  label: ReactNode;
  badge?: ReactNode;
  disabled?: boolean;
  /** Panel content; called only for the active tab. */
  render?: () => ReactNode;
}

export interface TabsProps<K extends string = string> {
  tabs: readonly TabItem<K>[];
  /** Accessible name of the tab list. */
  label: string;
  value?: K;
  defaultValue?: K;
  onChange?: (key: K) => void;
  className?: string;
  stripClassName?: string;
  panelClassName?: string;
  /** Render panels yourself (e.g. from `value`) instead of `render`. */
  children?: ReactNode;
}

export function Tabs<K extends string = string>({
  tabs,
  label,
  value,
  defaultValue,
  onChange,
  className,
  stripClassName,
  panelClassName,
  children,
}: TabsProps<K>) {
  const baseId = useId();
  const [inner, setInner] = useState<K | undefined>(defaultValue);
  const firstEnabled = tabs.find((t) => !t.disabled)?.key;
  const active = (value ?? inner ?? firstEnabled) as K | undefined;
  const activeTab = tabs.find((t) => t.key === active);
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const select = (key: K) => {
    if (value === undefined) setInner(key);
    if (key !== active) onChange?.(key);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = Math.max(
      0,
      tabs.findIndex((t) => t.key === active),
    );
    const next = nextTabIndex(
      e.key,
      current,
      tabs.map((t) => Boolean(t.disabled)),
    );
    if (next === null) return;
    e.preventDefault();
    const target = tabs[next];
    if (!target) return;
    refs.current[target.key]?.focus();
    select(target.key);
  };

  const tabId = (key: string) => `${baseId}-tab-${key}`;
  const panelId = `${baseId}-panel`;

  return (
    <div className={cn('space-y-4', className)}>
      <div
        aria-label={label}
        aria-orientation="horizontal"
        className={cn('flex flex-wrap items-center gap-1 border-b border-slate-200', stripClassName)}
        role="tablist"
        onKeyDown={onKeyDown}
      >
        {tabs.map((t) => {
          const selected = t.key === active;
          return (
            <button
              key={t.key}
              ref={(el) => {
                refs.current[t.key] = el;
              }}
              aria-controls={panelId}
              aria-selected={selected}
              className={cn(
                '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold',
                selected
                  ? 'border-action font-medium text-fg'
                  : 'border-transparent text-fg-secondary hover:text-fg',
                t.disabled && 'cursor-not-allowed opacity-60',
              )}
              disabled={t.disabled}
              id={tabId(t.key)}
              role="tab"
              tabIndex={selected ? 0 : -1}
              type="button"
              onClick={() => select(t.key)}
            >
              <span>{t.label}</span>
              {t.badge != null ? (
                <span
                  className={cn(
                    'ml-1 rounded-full px-1.5 py-0.5 text-xs font-medium',
                    selected ? 'bg-gold-soft text-gold-text' : 'bg-slate-100 text-fg-secondary',
                  )}
                >
                  {t.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div
        aria-labelledby={activeTab ? tabId(activeTab.key) : undefined}
        className={panelClassName}
        id={panelId}
        role="tabpanel"
        tabIndex={0}
      >
        {children ?? activeTab?.render?.() ?? null}
      </div>
    </div>
  );
}
